import { Component, effect, forwardRef, inject, signal } from '@angular/core';
import { AbstractFormControl } from '../abstract-form-control';
import {
  AbstractControl, AsyncValidator, NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { LabelItemDto, SensorDetailDto, SensorService } from '@artemis-software/wr-api';
import SensorUnitEnum = SensorDetailDto.SensorUnitEnum;
import { map, of, switchMap, take, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface SensorUnitDimensionSelectItem {
  label: LabelItemDto;
  sensorUnit: SensorUnitEnum;
}

@Component({
  standalone: false,
  selector: 'wr-sensor-unit-label-select',
  templateUrl: './sensor-unit-label-select.component.html',
  styleUrls: ['./sensor-unit-label-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SensorUnitLabelSelectComponent,
      multi: true,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => SensorUnitLabelSelectComponent),
      multi: true,
    },
  ],
})
export class SensorUnitLabelSelectComponent extends AbstractFormControl<SensorUnitDimensionSelectItem> implements AsyncValidator{
  readonly sensorService = inject(SensorService);

  constructor() {
    super();
    effect(() => {
      this.value = { label: this.label()!, sensorUnit: this.sensorUnit() };
    });
  }

  validate(control: AbstractControl){
    const label = this.label();
    if (!label) {
      return of<ValidationErrors | null>(null);
    }
    return timer(200).pipe(
      switchMap(() =>
        this.sensorService.validateSensorUnitWithLabel({
          sensorUnit: this.sensorUnit(),
          labelId: label.id,
        })
      ),
      map(isCompatible => (isCompatible ? null : { sensorUnitInvalid: true })),
      catchError(() => of<ValidationErrors>({ serverError: true })),
      take(1)
    );
  }

  readonly sensorUnit = signal<SensorUnitEnum>(SensorUnitEnum.None);
  readonly label = signal<LabelItemDto | undefined>(undefined);

  override writeValue(value: SensorUnitDimensionSelectItem | undefined) {
    super.writeValue(value);
    this.label.set(value?.label);
    this.sensorUnit.set(value?.sensorUnit ?? SensorUnitEnum.None);
  }
}
